<template>
  <div class="task-files">
    <div class="task-files__header">
      <TirPmFileUploader
        v-if="!files.length"
        :is-disabled="isDisabled"
        is-multiple
        @upload-files="emit('attachFiles', $event)"
      >
        <div :class="['task-files__header-row-empty', { 'is-disabled': isDisabled }]">
          <div>
            <span class="task-files__label">Файлы</span>
          </div>
          <div>
            <TirPmButton
              :size="TirPmButtonSizeEnum.Small"
              :variant="TirPmButtonVariantEnum.SecondaryState"
              :is-loading="isLoadingAttachButton"
              :is-disabled="isDisabled"
            >
              <template #leftSlot>
                <PaperClipIcon />
              </template>
              Выберите файл
            </TirPmButton>
          </div>
        </div>
      </TirPmFileUploader>
      <template v-else>
        <span class="task-files__label">Файлы</span>
        <TirPmFileUploader
          :is-disabled="isDisabled"
          is-multiple
          :max-size="10000 * 1024 * 1024"
          @upload-files="emit('attachFiles', $event)"
        >
          <TirPmButton
            :size="TirPmButtonSizeEnum.Small"
            :variant="TirPmButtonVariantEnum.SecondaryState"
            :is-disabled="isDisabled"
          >
            <template #leftSlot>
              <PaperClipIcon />
            </template>
            Выберите файл
          </TirPmButton>
        </TirPmFileUploader>
      </template>
    </div>
    <TirPmScrollbar class="task-files__scrollbar" enable-drag-scroll>
      <div class="task-files__files">
        <TirPmFilePreview
          class="task-files__file-preview"
          v-for="file in filesToPreview"
          :file="file"
          :files="filesToPreview"
          :files-loading-edit-name="filesLoadingEditName"
          :key="file.FileId"
          @on-click-delete="emit('removeFile', file.FileId)"
          @on-click-edit="emit('openEditImage', $event)"
          @save-name="emitUpdateName"
        />
      </div>
    </TirPmScrollbar>
  </div>
</template>

<script lang="ts" setup>
import { computed, PropType } from 'vue';
import {
  TirPmFileUploader,
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmScrollbar,
} from 'tir-components';
import { TaskFileModel } from '../../models';
import { PaperClipIcon } from 'tir-style-system/icons/outline';
import { PreviewFileModel } from './models';
import { FilePreviewAccessEnum, TirPmFilePreview } from 'tir-pm-file-preview';

const emit = defineEmits<{
  /**Событие прикрепления файлов */
  (e: 'attachFiles', files: Array<File>): void;
  /**Событие удаления файла */
  (e: 'removeFile', fileId: number): void;
  /**Событие обновления названия файла */
  (e: 'updateName', fileId: number, fileName: string): void;
  /**Событие открытия окна с редактированием изображения */
  (e: 'openEditImage', fileId: number): void;
}>();

const emitUpdateName = (fileId: number, fileName: string) => emit('updateName', fileId, fileName);

const props = defineProps({
  /**Список файлов */
  files: {
    type: Array as PropType<Array<TaskFileModel>>,
    default: [],
  },
  /**Заблокировать создание файлов */
  isDisabled: {
    type: Boolean,
    default: false,
  },
  /**Загрузка кнопки прикрепления*/
  isLoadingAttachButton: {
    type: Boolean,
    default: false,
  },
  /**Загрузка при редактировании названия */
  filesLoadingEditName: {
    type: Array as PropType<Array<number>>,
    default: [],
  },
});

const filesToPreview = computed(() => {
  const files = new Array<PreviewFileModel>();

  files.push(
    ...props.files.map((file) => {
      return new PreviewFileModel({
        FileId: file.Id,
        Url: file.Url,
        Name: file.Name,
        Size: file.Size,
        DateUpload: file.DateUpload,
        User: {
          Id: file.User.Id,
          FirstName: file.User.FirstName,
          LastName: file.User.LastName,
          MiddleName: file.User.MiddleName,
          AvatarColor: file.User.AvatarColor,
          Image: file.User.ImageUrl,
        },
        Progress: file.UploadProgress,
        IsProccessing: file.IsProccessing,
        VideoPreviewUrl: file.VideoPreviewUrl,
        Accesses:
          props.isDisabled || file.IsSendingLastChunk
            ? []
            : [FilePreviewAccessEnum.Edit, FilePreviewAccessEnum.Remove],
      });
    }),
  );

  return files.sort((a, b) => b.DateUpload.getTime() - a.DateUpload.getTime());
});
</script>

<style lang="scss" scoped>
.task-files {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.625rem;
  }
  &__header-row-empty {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.25rem;
    flex: 1;
    cursor: pointer;

    &.is-disabled {
      cursor: auto;
    }
  }
  &__label {
    color: var(--text-secondary, #686c73);
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1rem;
  }
  &__scrollbar {
    margin-top: 0.25rem;
  }
  &__files {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    width: fit-content;
    user-select: none;
    padding-bottom: 0.625rem;
  }
  &__file-preview {
    flex-shrink: 0;
  }
}
</style>
<style lang="scss">
.task-files__header .tir-pm-file-uploader {
  flex: 1;
}
</style>
